// src/components/UpcomingMeetingsWidget.jsx
// Widget compacto de "próximas reuniones" en la portada de Claim Status
// (ClaimStatusGate.jsx) — pedido explícito 2026-09-19: "necesito que las
// próximas reuniones se muestren en el claim status, afuera de Mi agenda".
// Mismos datos que ClaimAgendaPage.jsx (useAdjusterEvents), pero mostrando
// solo las próximas 5 en una franja chica en vez del calendario completo.
import React, { useContext, useState } from 'react';
import toast from 'react-hot-toast';
import { LanguageContext } from '../App';
import { EVENT_TYPE_LABELS } from '../services/claimEventService';
import { getChecksByClaimRecordId } from '../services/claimService';
import { useAdjusterEvents, isPastEvent } from '../hooks/useAdjusterEvents';
// Estilos de la versión en card (cards de los Resúmenes e Inicio).
import './Dashboard/OverviewCards.css';
import './Dashboard/AdjusterHome.css';

const formatShortDate = (date, language) => {
  if (!date) return '—';
  const parsed = new Date(`${date}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat(language === 'es' ? 'es-ES' : 'en-US', { month: 'short', day: 'numeric' }).format(parsed);
};

// variant="card": versión en card del inicio del ajustador (AdjusterHome),
// que se muestra aunque no haya citas.
// wide: citas en columnas (Resúmenes); onOpenSchedule: enlace "Ver agenda".
const UpcomingMeetingsWidget = ({ variant = 'strip', wide = false, onOpenSchedule = null }) => {
  const { language } = useContext(LanguageContext);
  const es = language === 'es';
  const monthKey = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  const { events, loading } = useAdjusterEvents(monthKey);
  const [openingClaim, setOpeningClaim] = useState(null);

  const upcoming = events.filter(e => !isPastEvent(e))
    .sort((a, b) => `${a.date}T${a.time || ''}`.localeCompare(`${b.date}T${b.time || ''}`))
    .slice(0, 5);

  const handleOpenEvent = async (event) => {
    if (event.claimRecordId) {
      setOpeningClaim(event.claimRecordId);
      try {
        const checks = await getChecksByClaimRecordId(event.claimRecordId);
        // Sin cheques (reclamo nuevo o migrado) se abre igual la ficha por su id.
        window.open(checks.length > 0
          ? `/claim-status/claim/${event.claimRecordId}?checks=${checks.map(c => c.id).join(',')}`
          : `/claim-status/claim/${event.claimRecordId}`, '_blank');
      } catch {
        toast.error(es ? 'No se pudo abrir el reclamo.' : 'Could not open the claim.');
      } finally {
        setOpeningClaim(null);
      }
      return;
    }
    if (event.zohoUrl) window.open(event.zohoUrl, '_blank');
  };

  const renderItem = event => {
    const label = event.type ? (EVENT_TYPE_LABELS[event.type]?.[es ? 'es' : 'en'] || event.type) : event.title;
    return (
      <button
        key={event.id}
        type="button"
        className={`cd-upcoming-item ${event.source === 'zoho' ? 'cd-upcoming-item-zoho' : ''}`}
        onClick={() => handleOpenEvent(event)}
        disabled={openingClaim === event.claimRecordId}
      >
        <span className="cd-upcoming-item-date">
          <strong>{formatShortDate(event.date, language)}</strong>
          {event.time && <span>{event.time}</span>}
        </span>
        <span className="cd-upcoming-item-body">
          <span className="cd-upcoming-item-label" title={typeof label === 'string' ? label : undefined}>{label}</span>
          {event.clientName && <span className="cd-upcoming-item-sub" title={event.clientName}>{event.clientName}</span>}
        </span>
        <i className="fa-solid fa-chevron-right cd-upcoming-item-arrow"></i>
      </button>
    );
  };

  if (variant === 'card') {
    return (
      <article className={`ov-card ah-meetings ${wide ? 'is-wide' : ''}`}>
        <div className="ah-card-head">
          <h3 className="ov-card-title">{es ? 'Próximas citas' : 'Upcoming appointments'}{!loading && upcoming.length > 0 && <span className="ah-meetings-count">{upcoming.length}</span>}</h3>
          {onOpenSchedule && <button type="button" className="ah-link" onClick={onOpenSchedule}>{es ? 'Ver agenda' : 'Open schedule'} →</button>}
        </div>
        {loading ? <div className="cd-loading" role="status"><div className="spinner"></div></div>
          : upcoming.length === 0 ? <p className="ov-foot"><span>{es ? 'No tienes citas próximas este mes.' : 'No upcoming appointments this month.'}</span></p>
          : <div className="cd-upcoming-widget-list">{upcoming.slice(0, wide ? 5 : 4).map(renderItem)}</div>}
      </article>
    );
  }

  if (loading) return null;
  if (upcoming.length === 0) return null;

  return (
    <details className="cd-container cd-upcoming-widget workspace-upcoming">
      <summary>{es ? 'Próximas citas' : 'Upcoming appointments'} <strong>{upcoming.length}</strong></summary>
      <div className="cd-upcoming-widget-head">
        <h2><i className="fa-regular fa-calendar"></i> {es ? 'Próximas reuniones' : 'Upcoming meetings'}</h2>
        <a href="/schedule" className="cd-upcoming-widget-link">{es ? 'Ver toda mi agenda' : 'View my full schedule'} <i className="fa-solid fa-arrow-right"></i></a>
      </div>
      <div className="cd-upcoming-widget-list">
        {upcoming.map(renderItem)}
      </div>
    </details>
  );
};

export default UpcomingMeetingsWidget;
